import React from 'react'
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import DescriptionIcon from '@mui/icons-material/Description'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { alpha, Theme } from '@mui/material/styles'
import { ThemeAITask } from '../../types/gotypes'
import { hasReusableSetup, REUSE_LABEL } from './reuseSetup'
import DisabledReason from '../system/DisabledReason'
import type { ActionDecision } from '../../utils/permissionReason'

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  active: 'success',
  archived: 'warning',
}

interface TaskRowProps {
  task: ThemeAITask
  /** Whether the viewer may archive the task, or why not. */
  canModify: (task: ThemeAITask) => ActionDecision
  onOpen: (task: ThemeAITask) => void
  onToggleArchive?: (task: ThemeAITask) => void
  onReuse?: (task: ThemeAITask) => void
  /** A Manager may star a task to or from Company-level (#1523 item 2), the
   *  same idiom as the code frame library and Data Viz — whoever owns it. */
  canPromote?: boolean
  onPromoteTask?: (task: ThemeAITask) => void
  onDemoteTask?: (task: ThemeAITask) => void
}

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  canModify,
  onOpen,
  onToggleArchive,
  onReuse,
  canPromote,
  onPromoteTask,
  onDemoteTask,
}) => {
  const sourceLabel = task.data_source?.type === 'upload'
    ? task.data_source.upload?.file_name || 'Uploaded file'
    : task.data_source?.type === 'job_variable'
      ? task.data_source.job_variable?.variable_name || 'Job variable'
      : 'No data source yet'
  const archiveLabel = task.status === 'archived' ? 'Unarchive' : 'Archive'
  const archive = canModify(task)
  const archiveButton = (
    <IconButton size="small" aria-label={`${archiveLabel} ${task.name}`} onClick={(e) => { e.stopPropagation(); onToggleArchive?.(task) }}>
      {task.status === 'archived' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
    </IconButton>
  )

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(task)}
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {canPromote && (onPromoteTask || onDemoteTask) && (
        <Tooltip title={task.visibility === 'exec' ? 'Demote from company-level' : 'Promote to company-level'}>
          <IconButton
            size="small"
            aria-label={task.visibility === 'exec' ? `Demote ${task.name} from company-level` : `Promote ${task.name} to company-level`}
            onClick={(e) => {
              e.stopPropagation()
              if (task.visibility === 'exec') onDemoteTask?.(task)
              else onPromoteTask?.(task)
            }}
          >
            {task.visibility === 'exec' ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{task.name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {task.data_source?.type === 'upload'
            ? <UploadFileIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            : <DescriptionIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
          <Typography variant="caption" color="text.secondary" noWrap>{sourceLabel}</Typography>
        </Box>
      </Box>
      <Chip label={task.status} size="small" color={STATUS_COLOR[task.status] || 'default'} variant="outlined" />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
        {task.updated_at ? new Date(task.updated_at).toLocaleDateString() : ''}
      </Typography>
      {/* Offered on every tier, colleagues' included: reusing someone's
          set-up makes a new task of your own and leaves theirs alone. */}
      {onReuse && hasReusableSetup(task) && (
        <Tooltip title={REUSE_LABEL}>
          <IconButton
            size="small"
            aria-label={`Use the set-up of ${task.name} on other data`}
            onClick={(e) => { e.stopPropagation(); onReuse(task) }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {/* Opens the task exactly as clicking the name does (#1523 item 1,
          Matt: "do I click the name in order to get into it?"). Offered on
          every tier, colleagues' tasks included. */}
      <Tooltip title="Open task">
        <IconButton size="small" aria-label={`Open task ${task.name}`} onClick={(e) => { e.stopPropagation(); onOpen(task) }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Shown on every tier; where the viewer cannot archive, disabled with
          the reason (Adam, 8 Oct). A click on it never opens the task. */}
      {onToggleArchive && (archive.allowed ? (
        <Tooltip title={archiveLabel}>{archiveButton}</Tooltip>
      ) : (
        <Box component="span" sx={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
          <DisabledReason reason={archive.reason}>{archiveButton}</DisabledReason>
        </Box>
      ))}
    </Paper>
  )
}

/** The Data Viz scope markers (Dashboards.tsx, 1 Sep call): company =
 *  primary, mine = info, colleagues = secondary, as a light tint plus a left
 *  edge, so the three tiers read apart the same way in both places. */
export type TaskTierTone = 'primary' | 'info' | 'secondary'

const TIER_TINT: Record<TaskTierTone, number> = { primary: 0.04, info: 0.05, secondary: 0.05 }

interface TaskTierSectionProps {
  title: string
  tone: TaskTierTone
  tasks: ThemeAITask[]
  /** Whether the viewer may archive a task, or why not (mirrors canModifyThemeAITask). */
  canModify: (task: ThemeAITask) => ActionDecision
  emptyLabel: string
  onOpenTask: (task: ThemeAITask) => void
  onToggleArchive?: (task: ThemeAITask) => void
  onReuse?: (task: ThemeAITask) => void
  /** A Manager may promote/demote any task in this tier to or from
   *  Company-level, whoever owns it — offered on every tier, colleagues'
   *  included (#1523 item 2). */
  canPromote?: boolean
  onPromoteTask?: (task: ThemeAITask) => void
  onDemoteTask?: (task: ThemeAITask) => void
}

export const TaskTierSection: React.FC<TaskTierSectionProps> = ({
  title,
  tone,
  tasks,
  canModify,
  emptyLabel,
  onOpenTask,
  onToggleArchive,
  onReuse,
  canPromote,
  onPromoteTask,
  onDemoteTask,
}) => (
  <Box
    component="section"
    aria-label={title}
    sx={{
      borderRadius: 1,
      p: 1,
      backgroundColor: (theme: Theme) => alpha(theme.palette[tone].main, TIER_TINT[tone]),
      borderLeft: (theme: Theme) => `4px solid ${theme.palette[tone].main}`,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {title}{' '}
      <Typography component="span" variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }}>
        ({tasks.length})
      </Typography>
    </Typography>
    {tasks.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{emptyLabel}</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            canModify={canModify}
            onOpen={onOpenTask}
            onToggleArchive={onToggleArchive}
            onReuse={onReuse}
            canPromote={canPromote}
            onPromoteTask={onPromoteTask}
            onDemoteTask={onDemoteTask}
          />
        ))}
      </Box>
    )}
  </Box>
)

export default TaskTierSection
